import { tryCatch } from "@/utils/try-catch";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useState, useContext, useCallback, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { AuthFlowType, FLOW_CONFIGS, FlowConfig, FlowType } from "@/config/AssociateIdentifierFlowConfig";
import { getFreshStartBackPath } from "@/config/FreshStartOriginConfig";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { skipAssociationService } from "@/services/shared/SkipAssociationService";
import { identifierAssociationService } from "@/services/shared/IdentifierAssociationService";

import { FormError } from "@/models/continente-credentials/common/FormError"

import { divideEmail } from "@/utils/EmailDivider";

import { Button } from "@/components/ui/button";
import CredentialsForm from "@/components/Form/Form";
import CredentialsHeader from "@/components/header/Header";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import UsernameInput from "@/components/username-input/UsernameInput";
import AltchaCaptcha from "@/components/altcha-captcha/AltchaCaptcha";


import { UserContext } from "@/contexts/continente-credentials/UserContext";
import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import { cn } from "@/utils/utils";
import { EmailAssociation as EmailAssociationProps, IdentifierAssociationRequest, PhoneNumberAssociation } from "@/models/shared/IdentifierAssociationRequest";
import { validatePortuguesePhoneNumber } from "@/utils/validatePortuguesePhoneNumber";

import {
  useCaptcha,
  siteKey,
} from "../../hooks/continente-credentials/useCaptcha";
import ButtonContainer from "@/components/button-container";

const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "";

interface AssociateIdentifierPageProps {
  flowConfig: FlowConfig;
}

type Form = IdentifierAssociationRequest<string> & FormError;

export default function SharedAssociateIdentifier({ flowConfig }: AssociateIdentifierPageProps) {
  const history = useHistory();
  const location = useLocation<any>();
  const { t } = useTranslation();
  const userContext = useContext(UserContext);

  const { handleResponse } = useApiResponseHandler();

  const digitalAssetContext = useContext(DigitalAssetContext);

  const requiredAssociation =
    (flowConfig.flow == AuthFlowType.EMAIL_ASSOCIATION && digitalAssetContext.digitalAsset.requiredEmailAssociation) ||
    (flowConfig.flow == AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION && digitalAssetContext.digitalAsset.requiredEmailAssociation) ||
    (flowConfig.flow == AuthFlowType.PHONE_ASSOCIATION && digitalAssetContext.digitalAsset.requiredPhoneNumberAssociation);

  const [loading, setLoading] = useState(false);
  const [isAdminBlocked, setAdminBlocked] = useState(false);
  const [identifierVisible, setIdentifierVisible] =
    useState<boolean>(false);
  const [identifier, setIdentifier] = useState<string>("");

  const userData = location.state?.properties || {};
  const userIdentifier = userData[flowConfig.identifierField] || "";
  const nextStep = location.state?.nextStep || "";
  const isSSO = userData.flowType === "SSO" || false;

  const captcha = useCaptcha(location.state);
  captcha.useGoogleRecaptcha();

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  useEffect(() => {
    userContext.updateUser({
      ...userContext.user,
      phoneNumberCode: "",
      phoneNumber: ""
    });
  }, [])

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleContactFormLinkClick = () => {
    history.push("/continente-credentials/faq" + ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION);
  };

  const skipAssociation = async () => {
    setLoading(true);
    const { data: response, error } = await tryCatch(
      skipAssociationService.skip(nextStep)
    );
    handleResponse(response, {
      setError,
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  const onSubmit: SubmitHandler<IdentifierAssociationRequest<string>> = async (data) => {
    // Depois de editar o email o desafio foi limpo: o backend repete requiredRecaptcha=true
    // e o estado nao muda, por isso e aqui que o widget tem de ser pedido outra vez.
    if (flowConfig.identifierField === 'email' && captcha.flowRequiresCaptcha && !captcha.isRequired) {
      captcha.requestCaptchaRequirement();
      return;
    }

    setLoading(true);

    let associationData: IdentifierAssociationRequest<string>;

    if (flowConfig.identifierField === 'email') {
      associationData = {
        identifierToAssociate: data.identifierToAssociate?.trim() ?? "",
        type: "email",
        recaptchaToken: captcha.getCaptchaToken(),
        requiredRecaptcha: location.state?.properties?.requiredRecaptcha ?? false,
      } as EmailAssociationProps<string>;
    } else {
      // For phone number association
      associationData = {
        identifierToAssociate: data.identifierToAssociate?.trim() ?? "",
        type: "phoneNumber",
        simplePhoneNumber: data.identifierToAssociate?.replace(userContext.user.phoneNumberCode, '') ?? "", // Remove country code
        phoneCountryCode: userContext.user.phoneNumberCode ?? "",
        email: userContext.user.email ?? "",
        recaptchaToken: captcha.getCaptchaToken(),
        userSessionId: location.state?.properties?.userSessionId,
      } as PhoneNumberAssociation<string>;
    }

    const { data: response, error } = await tryCatch(
      identifierAssociationService.associate(associationData, nextStep)
    );

    handleResponse(response, {
      setError,
      onSuccess: (response) => {
        userContext.updateUser({
          ...userContext.user,
          [flowConfig.identifierField]: data.identifierToAssociate?.trim() ?? "",
        });
        history.push(response.value.nextStep.substring(4), response.value);
      },
      setAdminBlocked,
    }, error);

    setLoading(false);
  };

  useEffect(() => {
    if (flowConfig.identifierField === 'email') {
      register("identifierToAssociate", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
          message: t("email_association.invalid_email"),
        },
      });
    } else if (flowConfig.identifierField === 'phoneNumber') {
      register("identifierToAssociate", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^\+?\d*$/,
          message: t("phonenumber_association.invalid_phonenumber"),
        },
        validate: {
          valid: (identifier) =>
            validatePortuguesePhoneNumber(identifier) ||
            t("common.error_invalid_pt_number"),
        },
      });
    }
  }, [register, t, flowConfig.identifierField]);

  const handleEmailOrPhoneNumberChange = useCallback(
    (input: string, phoneNumberCountryCode: string = "") => {
      clearErrors();

      if (flowConfig.identifierField === 'email') {
        // O token de reCAPTCHA e de uso unico: sem este reset o utilizador reenvia
        // sempre o mesmo token gasto depois de uma rejeicao.
        captcha.resetCaptchaRequirement();

        const cleanInput = input.replace(/\s+/g, "");
        setIdentifier(cleanInput);
        setValue("identifierToAssociate", cleanInput);
        setIdentifierVisible(cleanInput.length > 0);
      } else if (flowConfig.identifierField === 'phoneNumber') {
        const trimmedInput = input.trim();
        const cleanedInput = trimmedInput !== "" ? `${phoneNumberCountryCode}${trimmedInput}`.trim() : "";
        setIdentifier(trimmedInput);
        setValue("identifierToAssociate", cleanedInput);
        setIdentifierVisible(trimmedInput.length > 0);

        if (trimmedInput.length === 0) {
          phoneNumberCountryCode = "";
        }

        // Update user context with country code if needed
        if (phoneNumberCountryCode !== "") {
          userContext.updateUser({
            ...userContext.user,
            phoneNumberCode: phoneNumberCountryCode,
          });
        }
      }
    },
    [clearErrors, setValue, flowConfig.identifierField, userContext, captcha.resetCaptchaRequirement]
  );

  const handleBackButton = (): number => {
    const backSteps =
      location?.state?.previousStep === "validate-otp" ? 2 : flowConfig.previousStepBackXTimes;
    return backSteps;
  };

  return (
    <CredentialsForm
      id={`validate_password_form_${flowConfig.flow}`}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <CredentialsHeader
        title={t(flowConfig.labels.headerTitle)}
        subtitle={cn(
          t(flowConfig.labels.headerSubtitle ?? ""),
          (flowConfig.identifierField == "email" ? divideEmail(userIdentifier) : userIdentifier))
        }
        showLoading={loading}
        showBackButton={!isSSO}
        previousStepBackXTimes={handleBackButton()}
        backClickToPushPath={getFreshStartBackPath(userData.freshStartOrigin, digitalAssetContext.digitalAsset.clientId)}
      />
      <UsernameInput
        required
        ariaLabel={t("accessibility.signin.input")}
        type={flowConfig.identifierField == "email" ? "email" : "phone"}
        isFilled={identifierVisible}
        isError={!!(errors.identifierToAssociate || errors.blocked || errors.submitError)}
        errorMsg={
          errors.identifierToAssociate?.message ||
          errors.blocked?.message ||
          errors.submitError?.message ||
          ""
        }
        onChange={handleEmailOrPhoneNumberChange}
        disabled={loading}
        onInvalid={() =>
          setError("identifierToAssociate", { message: t("common.required_field") })
        }
        value={identifier}
        autocompleteWith={flowConfig.identifierField == "email" ? "email" : "phone"}
      />
      {errors.blocked && isAdminBlocked && (
        <div className="h-10 text-7xl font-bold text-center mb-30">
          <span
            onClick={handleContactFormLinkClick}
            className="text-black hover:underline cursor-pointer font-bold"
          >
            {t("common.contact_form")}
          </span>
        </div>
      )}
      <div aria-live="polite">
        {captcha.shouldShowRecaptcha && (
          <div
            id="g-recaptcha"
            className="flex justify-center"
            data-sitekey={siteKey}
          />
        )}
        {captcha.shouldShowAltcha && (
          <AltchaCaptcha
            onVerified={captcha.handleAltchaCaptchaVerified}
            onError={captcha.handleAltchaCaptchaError}
          />
        )}
      </div>
      <ButtonContainer>
        <Button
          disabled={loading}>
          {t(flowConfig.labels.nextButton)}
        </Button>
        {flowConfig.type === FlowType.AUTH && !requiredAssociation && (
          <Button
            type="button"
            variant={"secondary"}
            onClick={skipAssociation}
            disabled={loading}>
            {t(flowConfig.labels.skipButton ?? "")}
          </Button>
        )}
        <NeedHelpButton />
      </ButtonContainer>
    </CredentialsForm>
  );
}

export const createAssociateIdentifierPage = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedAssociateIdentifier flowConfig={config} />;
};

export const EmailAssociation = createAssociateIdentifierPage(AuthFlowType.EMAIL_ASSOCIATION);
export const PhoneNumberRegistrationEmailAssociation = createAssociateIdentifierPage(AuthFlowType.PHONE_REGISTRATION_EMAIL_ASSOCIATION);
export const PhoneAssociation = createAssociateIdentifierPage(AuthFlowType.PHONE_ASSOCIATION);
export const SCPhoneDefinition = createAssociateIdentifierPage(AuthFlowType.PHONE_DEFINITION);
export const SCEmailDefinition = createAssociateIdentifierPage(AuthFlowType.EMAIL_DEFINITION); 